import { FormSummarySection, WarningSummarySection } from '@form';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Api from '@services/api';
import { Button } from 'nhsuk-react-components';
import MissingMandatoryAnswersErrorSummary from './mandatory-question-error-summary';
import QuestionErrorSummary from './question-error-summary';
import ReactHtmlParser from 'react-html-parser';
import { createSummaryWarningListFromData } from '@util/warning-summary';
import { deflate } from 'pako';
import { enrichFormData } from '@util/view-submission';
import { getSkippableQuestions } from '@services/form-data-section';
import { getSubmissionSectionPayload } from '@util/api-payloads';
import { identifyMissingMandatoryAnswers } from '@util/mandatory-question-validation';
import { isEmpty } from 'lodash';
import { jsPDF } from 'jspdf';
import { setTitle } from '@util/browser';

const FormSummary = props => {
    const params = useParams();
    const history = useNavigate();
    setTitle(`Summary - ${props.collection.name}`);
    const [showError, setShowError] = useState(false);
    const [showValidationError, setShowValidationError] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [missingAnswers, setMissingAnswers] = useState([]);
    const [enrichedData, setEnrichedData] = useState([]);

    const applyConditionalRevealsFromSavedAnswers = questions => {
        let updatedQuestions = questions.map(question => ({ ...question }));

        updatedQuestions.forEach(question => {
            if (
                !question.options ||
                question.value === undefined ||
                question.value === null ||
                !['radio', 'select', 'checkbox'].includes(question.type)
            ) {
                return;
            }

            const selectedValues = Array.isArray(question.value)
                ? question.value
                : [question.value];

            const selectedOptions = question.options.filter(option =>
                selectedValues.includes(option.key)
            );

            const selectedQuestionIds = selectedOptions
                .flatMap(option => option?.questions || [])
                .map(childQuestion => childQuestion.id)
                .filter(Boolean);

            const unselectedQuestionIds = question.options
                .filter(option => !selectedValues.includes(option.key))
                .flatMap(option => option?.questions || [])
                .map(childQuestion => childQuestion.id)
                .filter(Boolean);

            updatedQuestions = updatedQuestions.map(childQuestion => {
                if (unselectedQuestionIds.includes(childQuestion.id)) {
                    return {
                        ...childQuestion,
                        hidden: true,
                        includeInApiPayload: false,
                        showOnSummaryPage: false
                    };
                }

                if (selectedQuestionIds.includes(childQuestion.id)) {
                    return {
                        ...childQuestion,
                        hidden: false,
                        includeInApiPayload: true
                    };
                }
                return childQuestion;
            });
        });
        return updatedQuestions;
    };

    useEffect(() => {
        const enriched = enrichFormData(props.data, props.collection).map(section => ({
            ...section,
            questions: applyConditionalRevealsFromSavedAnswers(section.questions)
        }));

        setEnrichedData(enriched);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data, props.collection]);

    useEffect(() => {
        setMissingAnswers(identifyMissingMandatoryAnswers(enrichedData));
    }, [enrichedData]);

    useEffect(() => {
        if (missingAnswers.length > 0) {
            setShowError(true);
        } else {
            setShowError(false);
        }
    }, [missingAnswers]);

    const handleClick = async () => {
        const { data, collection, submissionId } = props;
        const dataSections = getDataSections(data);
        if (missingAnswers.length <= 0) {
            const payload = createPayload(dataSections);
            if (collection.name.includes('(eDec)')) {
                await handlePdfSubmission(payload, dataSections, submissionId);
            } else {
                await submitPayload(payload, submissionId);
                navigateToCompleted();
            }
        }
        window.scrollTo(0, 0);
    };

    const getDataSections = data => {
        return !isEmpty(getSkippableQuestions(data)) ? data.filter(({ show }) => show) : data;
    };

    const createPayload = dataSections => {
        const sections = dataSections.map(({ id, questions }) => ({
            id,
            questions: getSubmissionSectionPayload(questions)
        }));
        return { sections };
    };

    const handlePdfSubmission = async (payload, dataSections, submissionId) => {
        const email = await getEmail(payload);
        const pdf = await generatePdf(dataSections, email);
        const pdfBlob = pdf.output('blob');
        const compressedPdf = await compressPdf(pdfBlob);

        payload.pdfBlob = compressedPdf;
        const response = await submitPayload(payload, submissionId);

        if (response.validation_errors && response.validation_errors.length > 0) {
            setValidationErrors(response.validation_errors);
            setShowValidationError(true);
        } else {
            navigateToCompleted();
        }
    };

    const generatePdf = async (dataSections, email) => {
        const pdf = new jsPDF('p', 'pt', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;

        let currentY = margin;

        const logoImageOptions = {
            x: 480,
            y: 20,
            width: 100,
            height: 40.63 //Scaled based on original image dimensions - 320x130 pixels https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/National_Health_Service_%28England%29_logo.svg/320px-National_Health_Service_%28England%29_logo.svg.png
        };
        var logoImageData =
            'data:image/png;base64,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';

        // Add NHS Logo
        pdf.addImage(
            logoImageData,
            'PNG',
            logoImageOptions.x,
            logoImageOptions.y,
            logoImageOptions.width,
            logoImageOptions.height
        );

        // Add Collection Name
        pdf.setFontSize(20);
        pdf.text(props.collection.name, 20, logoImageOptions.y + 30);

        currentY = margin + 20;

        // Add Submitting Organisation Name and Code
        pdf.setFontSize(16);
        pdf.text(props.orgName, 20, logoImageOptions.y + 60);

        currentY += 70;

        // Add footer to PDF - current date and time
        const addFooter = () => {
            pdf.saveGraphicsState();
            pdf.setGState(new pdf.GState({ opacity: 0.6 }));
            pdf.setFontSize(10);
            pdf.text(`Submitter: ${email}`, 20, pageHeight - 10);
            pdf.text(getCurrentDateTime(), 480, pageHeight - 10);
            pdf.restoreGraphicsState();
        };
        addFooter();

        // Adds question text onto the page, making sure to break onto a newline when a line length reaches a certain length
        const addTextWithPageBreaks = (text, fontSize, yOffset) => {
            pdf.setFontSize(fontSize);
            const lines = pdf.splitTextToSize(text, pageWidth - 2 * margin);
            lines.forEach(line => {
                if (currentY + fontSize > pageHeight - margin) {
                    pdf.addPage();
                    addFooter();
                    currentY = margin; // Reset y position after adding a new page
                }
                pdf.text(line, margin, currentY + yOffset);
                currentY += fontSize + 5; // Adjust spacing between lines
            });
        };

        // Custom question used for displaying practice opening and closing times
        /* This will layout the answers like this:
                    AM Open     AM Close     PM Open     PM Close    
            Mon     00:00       00:00        00:00       00:00
            Tue     00:00       00:00        00:00       00:00
            Wed     00:00       00:00        00:00       00:00
            Thu     00:00       00:00        00:00       00:00
            Fri     00:00       00:00        00:00       00:00
            Sat     00:00       00:00        00:00       00:00
            Sun     00:00       00:00        00:00       00:00
        */
        const addOpenCloseTimes = (text, fontSize) => {
            // Print the header
            pdf.text('AM Open', 100, currentY);
            pdf.text('AM Close', 200, currentY);
            pdf.text('PM Open', 300, currentY);
            pdf.text('PM Close', 400, currentY);
            currentY += fontSize + 10; // Adjust spacing between lines

            const days = [
                'monday',
                'tuesday',
                'wednesday',
                'thursday',
                'friday',
                'saturday',
                'sunday'
            ];
            // This is done as the days list in the JSON has been sorted alphabetically, so we cant just loop through it.
            days.forEach(day => {
                if (currentY + 15 > pageHeight - margin) {
                    pdf.addPage();
                    addFooter();
                    currentY = margin; // Reset y position for the new page
                }
                const dayText = text[day];
                pdf.text(day.charAt(0).toUpperCase() + day.slice(1, 3), 20, currentY);
                pdf.text(dayText.am_open ? dayText.am_open : ' ', 100, currentY);
                pdf.text(dayText.am_close ? dayText.am_close : ' ', 200, currentY);
                pdf.text(dayText.pm_open ? dayText.pm_open : ' ', 300, currentY);
                pdf.text(dayText.pm_close ? dayText.pm_close : ' ', 400, currentY);
                currentY += fontSize + 5;
            });
        };

        /*
            Loops through each section of the collection, writes out the name of the section
            and then writes out each question in that section along with the answers given to
            those questions.
        */
        dataSections.forEach(item => {
            pdf.setFontSize(15);
            addTextWithPageBreaks(item.name, 15, 10);

            const questions = item.questions;
            pdf.setFontSize(12);
            currentY += 15;

            questions.forEach(question => {
                if (
                    question.name &&
                    question.value != null &&
                    question.value !== undefined &&
                    question.value !== ''
                ) {
                    addTextWithPageBreaks(question.name, 12, 0);
                    currentY += 5;

                    if (question.type === 'open-close-times') {
                        addOpenCloseTimes(question.value, 12);
                    } else {
                        addTextWithPageBreaks(
                            formatAnswer(question.value, question.type, question.options),
                            12,
                            0
                        );
                    }

                    currentY += 15;
                }
            });

            currentY += 20; // Space after the questions

            // Check if we need a new page before starting the next section
            if (currentY + 15 > pageHeight - margin) {
                pdf.addPage();
                addFooter();
                currentY = margin; // Reset y position for the new page
            }
        });

        return pdf;
    };

    function getCurrentDateTime() {
        const now = new Date();

        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const year = now.getFullYear();

        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');

        return `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;
    }

    // Answer sanitization due to stored values not always being human readable.
    const formatAnswer = (answers, type, options) => {
        var answerText = answers;
        switch (type) {
            case 'text':
                answerText = capitalizeString(answerText);
                break;
            case 'radio':
            case 'select':
                if (answers === 'na') {
                    answerText = 'N/A';
                } else {
                    answerText = options.find(option => option.key === answers).value;
                }
                break;
            case 'checkbox':
                // This will turn: ['option-1','option-2','option-3']
                // Into: Option 1, Option 2, Option 3
                if (typeof answers === 'object') {
                    var answerList = [];
                    answers.forEach(answer => {
                        var selectedAnswer = options.find(option => option.key === answer);
                        if (selectedAnswer !== undefined) {
                            answerList.push(selectedAnswer.value);
                        }
                    });
                    answerText = answerList.join(', ');
                } else {
                    answerText = capitalizeString(answerText);
                }

                break;
            case 'date':
                // This will turn: 2024-07-25T10:22:26.742Z
                // Into: 25/07/2024
                answerText = answers.day + '/' + answers.month + '/' + answers.year;
                break;
            default:
                answerText = answers;
        }
        const formattedAnswer = 'Answer: ' + answerText.toString();
        return formattedAnswer;
    };

    const capitalizeString = string => {
        return string[0].toUpperCase() + string.slice(1);
    };

    // The PDF is compressed to make it easier to transport over API to the Lambda function
    const compressPdf = pdfBlob => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const pdfArrayBuffer = reader.result;
                const compressedPdf = deflate(pdfArrayBuffer);
                resolve(compressedPdf);
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(pdfBlob);
        });
    };

    const submitPayload = async (payload, submissionId) => {
        const api = new Api();
        const response = await api.putSubmission(payload, submissionId);
        return response;
    };

    const getEmail = async payload => {
        const api = new Api();
        payload.email = true;
        const response = await api.putSubmission(payload, 'email');
        return response.email;
    };

    const navigateToCompleted = () => {
        const path = `/${params.collection}/completed`;
        history(path);
    };

    const warningList = createSummaryWarningListFromData(props.data);

    const header = props.collection.form_summary_header;
    const headerHtml = header ? atob(header) : undefined;
    return (
        <div className="nhsuk-grid-row">
            <div className="nhsuk-grid-column-full">
                <h1>Check your answers before sending</h1>
                {showError && (
                    <MissingMandatoryAnswersErrorSummary
                        missingMandatoryAnswers={missingAnswers}
                        collection={params.collection}
                    />
                )}
                {showValidationError && (
                    <QuestionErrorSummary validationIssues={validationErrors} />
                )}
                {ReactHtmlParser(headerHtml)}
                {!isEmpty(warningList) && <WarningSummarySection warningList={warningList} />}
                {enrichedData
                    .filter(x => x.show)
                    .map(section => (
                        <FormSummarySection
                            key={section.index}
                            onChangeClick={props.onChangeClick}
                            orgCode={props.orgCode}
                            section={section}
                            showChangeLink={true}
                        />
                    ))}
                <Button onClick={handleClick}>Submit</Button>
            </div>
        </div>
    );
};

export default FormSummary;
